import { RecordType } from '@xbcb/shared-types';
import { WorkOrderType } from '@xbcb/work-order-types';
import CustomsEntrySecondaryHeader from 'components/CustomsEntrySecondaryHeader';
import { CustomsDeclaration } from '../../../loadableComponents';
import {
  getSharedCustomsEntrySubmitButtons,
  sharedCustomsEntryKebabMenuItems,
} from '../../utils';
import { WorkOrderRoute } from '../.';
import { sendDocumentsToBroker } from './kebabMenuItems';

import {
  queryCustomsDeclaration,
  updateCustomsDeclaration,
} from './customsDeclaration.query';
import { transformCustomsDeclaration } from '../../../../libs/formTransforms/workOrder/customsDeclaration';
import { isCuDeCommercialInvoiceTabEnabled } from 'featureFlags';
import { getAllGoodsShipmentSnapshots } from 'libs/formCore/transforms/goodsShipmentFormRef';
import { getEditedGoodsItemsForShipment } from 'libs/formCore/transforms/goodsItemFormRef';
import { goodsItemDetailSaveSchema } from 'libs/formCore/schemas/forms/goodsItemDetail';
import { goodsShipmentSaveSchema } from 'libs/formCore/schemas/forms/goodsShipment';
import { GoodsItemDetailTransformer } from 'libs/formCore/transforms/GoodsItemDetailTransformer';
import {
  validationErrorStore,
  itemErrorKey,
  shipmentErrorKey,
} from 'libs/formCore/validation';

const customsDeclarationRoute: WorkOrderRoute = {
  Page: CustomsDeclaration,
  recordType: RecordType.CUSTOMS_DECLARATION,
  queries: { get: queryCustomsDeclaration, update: updateCustomsDeclaration },
  SecondaryHeaderContents: [CustomsEntrySecondaryHeader],
  transformUpdateRecordInput: transformCustomsDeclaration.toSchema,
  defaultEditMode: isCuDeCommercialInvoiceTabEnabled,
  validateUpdate: async () => {
    const errors: { title: string; messages: string[] }[] = [];
    validationErrorStore.clearAll();

    const snapshots = getAllGoodsShipmentSnapshots();
    const itemTransformer = new GoodsItemDetailTransformer();

    for (const [idx, snapshot] of snapshots) {
      // Validate shipment fields
      const shipmentResult = goodsShipmentSaveSchema.safeParse(
        snapshot.formData,
      );
      if (!shipmentResult.success) {
        for (const issue of shipmentResult.error.issues) {
          validationErrorStore.set(
            shipmentErrorKey(idx, issue.path.join('.')),
            { message: issue.message, source: 'schema' },
          );
        }
        errors.push({
          title: `Goods Shipment ${idx + 1}`,
          messages: shipmentResult.error.issues.map((i) => i.message),
        });
      }

      // Validate goods items for this shipment
      const editedItems = getEditedGoodsItemsForShipment(idx);
      const allItems = snapshot.original.goodsItem ?? [];

      for (const item of allItems) {
        const sequence = item.sequence!;
        const editedEntry = editedItems.get(sequence);

        // Use edited form data if visited, otherwise round-trip through transformer
        const formData = editedEntry
          ? editedEntry.formData
          : itemTransformer.toForm(item);

        const result = goodsItemDetailSaveSchema.safeParse(formData);
        if (!result.success) {
          for (const issue of result.error.issues) {
            validationErrorStore.set(
              itemErrorKey(idx, sequence, issue.path.join('.')),
              { message: issue.message, source: 'schema' },
            );
          }
          errors.push({
            title: `Goods Item ${sequence} (Shipment ${idx + 1})`,
            messages: result.error.issues.map((i) => i.message),
          });
        }

        // Check for adjustment codes that conflict with shipment-level selections
        const shipmentCodes = new Set(
          (snapshot.formData.adjustments ?? [])
            .map((a) => a.type)
            .filter(Boolean) as string[],
        );
        if (shipmentCodes.size > 0) {
          const conflictMessages: string[] = [];
          (formData.valuationAdjustments ?? []).forEach((a, i) => {
            if (a.type && shipmentCodes.has(a.type)) {
              const msg = `${a.type} is already applied at shipment level`;
              validationErrorStore.set(
                itemErrorKey(idx, sequence, `valuationAdjustments.${i}.type`),
                { message: msg, source: 'conflict' },
              );
              conflictMessages.push(msg);
            }
          });
          if (conflictMessages.length) {
            errors.push({
              title: `Goods Item ${sequence} (Shipment ${idx + 1})`,
              messages: conflictMessages,
            });
          }
        }
      }
    }

    return errors.length ? { additionalErrors: errors } : {};
  },
  kebabMenuItems: [...sharedCustomsEntryKebabMenuItems, sendDocumentsToBroker],
  submitButtons: getSharedCustomsEntrySubmitButtons(
    WorkOrderType.CustomsDeclaration,
  ),
};

export default customsDeclarationRoute;
